import { useSuspenseQuery } from '@apollo/client';
import { GET_ALERT_CONFIGURATION } from '@features/deviceAlertsModal/graphql/queries/getAlertConfiguration';
import { Box, FormControl, InputLabel, MenuItem, Select, Table, TableCell, TableHead, TableRow, Tabs, Tab, Badge, useTheme } from '@mui/material';
import { useMemo, useState, type FC } from 'react';
import { FormValues, UpdateAlertConfigurationInputSchema } from './updateSchema';
import { TabContent, TabProps } from '../common';
import { useLingui } from '@lingui/react';
import { useModalStatusMessage } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, DefaultValues, useForm } from 'react-hook-form';
import { AlertConfiguration, DeviceAlertComparisonType, DeviceAlertMetricName, DeviceAlertState, MetricField, MetricUnit } from '@/__generated__/graphql';
import { getUnitsPerMetric } from '@/utils/getUnitsPerMetric';
import { AlertStateRow } from './AlertStateRow';
import { getMetricDisplayName } from '@/utils/i18n/getMetricDisplayName';
import { useMutation } from '@apollo/client';
import { UPDATE_ALERT_CONFIGURATION } from './updateMutation';
import { msg } from '@lingui/macro';

const validMetricFields = [MetricField.Level, MetricField.Flow];

const cleanNulls = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(cleanNulls);
  } else if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [k, cleanNulls(v)])
    );
  }
  return obj;
};

const normalizeState = (state: any) => ({
  comparisonType: state?.comparisonType ?? DeviceAlertComparisonType.Above,
  eventName: state?.eventName ?? '',
  thresholdValue: state?.thresholdValue ?? null,
  thresholdMax: state?.thresholdMax ?? null,
  thresholdMin: state?.thresholdMin ?? null,
  notificationDelaySec: state?.notificationDelaySec ?? 28800,
  enabled: state != null
});

const getDefaultValues = (
  deviceId: string,
  data: AlertConfiguration[],
  metric: MetricField
): DefaultValues<FormValues> => {
  const config = data.find(c => c.metric === metric as unknown as DeviceAlertMetricName);
  const unit = config?.ok?.metricUnit || config?.warning?.metricUnit || config?.critical?.metricUnit || undefined;
  const alertConfiguration = {
    deviceId,
    unit,
    metric: metric as unknown as DeviceAlertMetricName,
    ok: normalizeState(config?.ok),
    warning: normalizeState(config?.warning),
    critical: normalizeState(config?.critical),
  };
  return alertConfiguration;
}

export const FormComponent: FC<TabProps> = ({ deviceId, onClose, setIsDirty }) => {
  const theme = useTheme();
  const { _, i18n } = useLingui();
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage()
  const { data } = useSuspenseQuery(GET_ALERT_CONFIGURATION, {
    variables: { deviceId },
  });
  const [mutate] = useMutation(UPDATE_ALERT_CONFIGURATION, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Configuración de alertas actualizada correctamente`));
      methods.reset(methods.getValues());
    },
    onError(error) {
      openModalErrorMessage(
        _(msg`Error al actualizar la configuración de alertas: ${error.message}`),
      );
    },
  });

  const handleMetricTabChange = (_: any, newValue: MetricField) => {
    setSelectedMetric(newValue);

    const values = getDefaultValues(deviceId, activeConfigurations, newValue);
    methods.reset(values);
  };

  const deviceMetrics = data.device.metrics?.availableFields;
  const { activeConfigurations } = data.device.alerts;

  const [selectedMetric, setSelectedMetric] = useState<MetricField>(
    activeConfigurations[0]
      ? (activeConfigurations[0].metric as unknown as MetricField)
      : (metricFieldsOptions[0]?.value as MetricField)
  );

  const metricFieldsOptions =
    deviceMetrics.filter((i) => validMetricFields.includes(i))
      .map((i) => ({
        label: getMetricDisplayName(i, i18n),
        value: i,
      })) ?? [];


  const unitOptions = selectedMetric
    ? getUnitsPerMetric(selectedMetric as unknown as MetricField, i18n).map(i => ({
      label: i.label,
      value: i.enumValue
    }))
    : [];

  const defaultValues = useMemo(
    () => getDefaultValues(deviceId, activeConfigurations, selectedMetric),
    [deviceId, activeConfigurations, selectedMetric]
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(UpdateAlertConfigurationInputSchema),
  });

  const onSubmit = methods.handleSubmit((data: FormValues) => {
    const { unit, ...rest } = data;
    const shouldIncludeState = (state: any) => {
      if (state?.enabled) {
        const { enabled, ...rest} = state;
        return cleanNulls({ ...rest, unit });
      } else {
        return undefined;
      }
    };

    const newData = {
      ...rest,
      ok: shouldIncludeState(data.ok),
      warning: shouldIncludeState(data.warning),
      critical: shouldIncludeState(data.critical),
    };
    mutate({ variables: { input: newData } });
  });

  // Lógica para saber si una métrica tiene configuración activa
  const hasConfig = (metricValue: MetricField) =>
    !!activeConfigurations.find((c) => c.metric === metricValue);

  return (
    <>
      <form onSubmit={onSubmit}>
        <TabContent
          slotProps={{
            modalActions: {
              onClose: onClose,
              onSubmit: 'submit',
              dirtyForm: methods.formState.isDirty,
              onResetForm: () => methods.reset()
            }
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Menú lateral de métricas */}
            <Tabs
              orientation="vertical"
              value={selectedMetric}
              onChange={handleMetricTabChange}
              sx={{
                borderRight: 1,
                borderColor: 'divider',
                minWidth: 180,
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              {metricFieldsOptions.map((metric) => (
                <Tab
                  disableRipple
                  key={metric.value}
                  value={metric.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span
                        style={{
                          color: hasConfig(metric.value) ? '#fff' : '#aaa',
                          fontWeight: hasConfig(metric.value) ? 'bold' : 'normal',
                        }}
                      >
                        {metric.label}
                      </span>
                    </Box>
                  }
                  sx={{
                    border: selectedMetric === metric.value ? `2px solid ${theme.palette.primary.main}` : 'none',
                    borderRadius: 1,
                    color: hasConfig(metric.value) ? '#fff' : '#aaa',
                    minHeight: 48,
                    alignItems: 'flex-start',
                    backgroundColor: 'transparent',
                  }}
                />
              ))}
            </Tabs>

            {/* Panel derecho: formulario de la métrica seleccionada */}
            <Box sx={{ flex: 1 }}>
              <Controller
                control={methods.control}
                name="unit"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="unit-dropdown">Unidad de medida</InputLabel>
                    <Select
                      {...field}
                      labelId="unit-dropdown"
                      label="Unidad de medida"
                    >
                      {unitOptions.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />

              <Table
                sx={{
                  borderWidth: 0,
                  borderTopWidth: 1,
                  borderLeftWidth: 1,
                  borderRightWidth: 1,
                  borderStyle: 'solid',
                  borderColor: theme.palette.divider,
                  mt: 2,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Estado de Alerta</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell colSpan={3}>Parámetros</TableCell>
                    <TableCell key='enabled' ></TableCell>
                  </TableRow>
                </TableHead>
                <AlertStateRow
                  control={methods.control}
                  setValue={methods.setValue}
                  alertState={DeviceAlertState.Ok} 
                />
                <AlertStateRow
                  control={methods.control}
                  setValue={methods.setValue}
                  alertState={DeviceAlertState.Warning}
                />
                <AlertStateRow
                  control={methods.control}
                  setValue={methods.setValue}
                  alertState={DeviceAlertState.Critical}
                />
              </Table>
            </Box>
          </Box>
        </TabContent>
      </form>
    </>
  );
};

